import { Dispatch, SetStateAction } from 'react'

import { FirebaseError } from 'firebase/app'
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ListResult,
  ref,
  StorageReference,
} from 'firebase/storage'

import { TRAINING_SUBFOLDER_SLUGS } from '../../constants/tracks'
import { handleErrors } from '../../utilFunctions/firebaseErrors'
import { storage } from '../firebaseConfig'

export type TrackAudioFile = { title: string; url: string }

/** Audios bajo `tracks/` (bitácoras nuevas). */
export type TracksAudioIndex = {
  chicas: Record<string, TrackAudioFile[]>
  chicos: Record<string, TrackAudioFile[]>
  improvisacion: TrackAudioFile[]
}

async function listAudioFilesInFolder(
  folderRef: StorageReference,
): Promise<TrackAudioFile[]> {
  const res = await listAll(folderRef)
  return Promise.all(
    res.items.map(async (file) => ({
      title: file.name,
      url: await getDownloadURL(file),
    })),
  )
}

/** Lista `tracks/chicas|chicos/<subcarpeta>/` y archivos en `tracks/improvisacion/`. */
export async function buildTracksAudioIndex(): Promise<TracksAudioIndex> {
  const chicas: Record<string, TrackAudioFile[]> = {}
  const chicos: Record<string, TrackAudioFile[]> = {}

  await Promise.all(
    TRAINING_SUBFOLDER_SLUGS.map(async (sub) => {
      const chicasRef = ref(storage, `tracks/chicas/${sub}`)
      const chicosRef = ref(storage, `tracks/chicos/${sub}`)
      try {
        chicas[sub] = await listAudioFilesInFolder(chicasRef)
      } catch {
        chicas[sub] = []
      }
      try {
        chicos[sub] = await listAudioFilesInFolder(chicosRef)
      } catch {
        chicos[sub] = []
      }
    }),
  )

  let improvisacion: TrackAudioFile[] = []
  try {
    improvisacion = await listAudioFilesInFolder(
      ref(storage, 'tracks/improvisacion'),
    )
  } catch {
    improvisacion = []
  }

  return { chicas, chicos, improvisacion }
}

/** Índice legacy (carpetas raíz antiguas) para bitácoras ya guardadas. */
export async function buildLegacyExerciseAudioIndex(): Promise<
  Record<string, { title: string; url: string }[]>[] | null
> {
  try {
    const larFolderRef = ref(storage, 'estimulación-laríngea')
    const resFolderRef = ref(storage, 'trabajo-respiratorio')
    const resonanFolderRef = ref(storage, 'trabajo-resonancial')

    const [_larFiles, _resFiles, _resonanFiles] = await Promise.all([
      listAll(larFolderRef),
      listAll(resFolderRef),
      listAll(resonanFolderRef),
    ])

    const [larFiles, resFiles, resonanFiles] = await Promise.all([
      getFileByCategory(_larFiles),
      getFileByCategory(_resFiles),
      getFileByCategory(_resonanFiles),
    ])

    return [larFiles, resFiles, resonanFiles]
  } catch (err) {
    console.warn('Legacy exercise audio index unavailable:', err)
    return null
  }
}

export const listAllStorage = async (folderRef: StorageReference) => {
  listAll(folderRef)
    .then((res) => {
      const files = res.items
      const promises = files.map((itemRef) => {
        // Obtén el enlace de descarga de cada archivo
        return getDownloadURL(itemRef).then((downloadURL) => {
          return {
            name: itemRef.name,
            downloadLink: downloadURL,
          }
        })
      })
      return Promise.all(promises)
    })
    .then((results) => {
      return results
    })
    .catch((error) => {
      return error
    })
}

export const getFileByCategory = async (files: ListResult) => {
  const filesByType: Record<string, { title: string; url: string }[]> = {}

  for (const folder of files.prefixes) {
    const gender = folder.name
    const folderFiles = await listAll(folder)

    const mapped = await Promise.all(
      folderFiles.items.map(async (file) => ({
        title: file.name,
        url: await getDownloadURL(file),
      })),
    )

    filesByType[gender] = mapped
  }

  return filesByType
}

// NO SE USA
export const downloadFile = async (
  fileName: string,
  userId: string,
  setError?: Dispatch<SetStateAction<string | null>>,
) => {
  try {
    const fileRef = ref(storage, `${userId}/${fileName}`)

    const downloadUrl = await getDownloadURL(fileRef)
    return downloadUrl
    // TODO: replace lines 114 and 115 with
    // const url = await guardedCall(() => getDownloadURL(fileRef))
    // return url
  } catch (error) {
    console.error('Error al descargar el archivo:', error)
    setError && `Error al descargar el archivo: ${error}`
  }
}

export const removeFile = async (
  refId: string,
  setError?: Dispatch<SetStateAction<string | null>>,
) => {
  try {
    const imgRef = ref(storage, refId)
    await deleteObject(imgRef)
  } catch (error) {
    const { code, message } = error as FirebaseError
    const friendly = handleErrors(code)
    console.error('Error al eliminar el archivo:', error)
    setError && setError(friendly || message)
  }
}
